import { type FunctionComponent, Fragment } from 'react';
import {
	DEFAULT_HOME_CAROUSEL_SMALL_CARD_MIN_WIDTH,
	HOME_CAROUSEL_SMALL_OPTIONS,
	DEFAULT_HOME_CAROUSEL_LARGE_CARD_MIN_WIDTH,
	HOME_CAROUSEL_LARGE_OPTIONS,
	DEFAULT_HOME_CAROUSEL_MEDIUM_CARD_MIN_WIDTH,
	HOME_CAROUSEL_MEDIUM_OPTIONS,
	EXCLUSIVE_DEALS_CAROUSEL_SMALL_CARD_MIN_WIDTH,
	EXCLUSIVE_DEALS_CAROUSEL_MEDIUM_CARD_MIN_WIDTH,
	EXCLUSIVE_DEALS_CAROUSEL_LARGE_CARD_MIN_WIDTH
} from '../../../constants/home/general';
import type { CarouselItemContent } from '../../../types/construct.types';
import { Carousel } from '../../carousel/carousel.component';
import { HomeCarouselOptionCard, type HomeCarouselOptionCardProps } from './home-carousel-option-card/home-carousel-option-card.component';

export type HomeCarouselProps = {
	items: CarouselItemContent[];
	CardComponent?: FunctionComponent<HomeCarouselOptionCardProps>;
	title?: string;
	automationHook?: string;
	navigationVerticalPosition?: number;
	cardMinWidth?: number;
	sectionClassName?: string;
	rootMargin?: string;
	widerCards?: boolean;
};

export const HomeCarousel: FunctionComponent<HomeCarouselProps> = ({
	items,
	title,
	CardComponent = HomeCarouselOptionCard,
	automationHook,
	navigationVerticalPosition,
	cardMinWidth,
	sectionClassName = 'list ma0 pa0 flex flex-column justify-between w-100',
	rootMargin,
	widerCards = false
}) => {
	const defaultCardWidths = {
		smallCard: widerCards ? EXCLUSIVE_DEALS_CAROUSEL_SMALL_CARD_MIN_WIDTH : DEFAULT_HOME_CAROUSEL_SMALL_CARD_MIN_WIDTH,
		mediumCard: widerCards ? EXCLUSIVE_DEALS_CAROUSEL_MEDIUM_CARD_MIN_WIDTH : DEFAULT_HOME_CAROUSEL_MEDIUM_CARD_MIN_WIDTH,
		largeCard: widerCards ? EXCLUSIVE_DEALS_CAROUSEL_LARGE_CARD_MIN_WIDTH : DEFAULT_HOME_CAROUSEL_LARGE_CARD_MIN_WIDTH
	};

	const { smallCard, mediumCard, largeCard } = defaultCardWidths;

	return items.length > 0 ? (
		<section
			className={`${sectionClassName} ${widerCards && items.length <= 3 ? 'items-center-ns' : ''}`}
			data-automation={automationHook}>
			{title && (
				<div className="flex flex-column items-center w-100">
					<h3 className="font-light f2-ns f3 mb3 mt0">{title}</h3>
				</div>
			)}

			{/* mobile */}
			<div className="db dn-ns">
				<Carousel
					shouldLockNavigation={widerCards && items.length <= 3 ? false : true}
					navigationVerticalPosition={navigationVerticalPosition}
					// converting from rem to px, plus 8 accounting for padding between the cards
					cardWidth={16 * (cardMinWidth ?? smallCard) + 8}>
					{items.map((item, index) => (
						<div
							key={`${item.fields?.title}-${index}`}
							data-testid={`${item.fields?.title}-${index}`}
							className="mh1 mh2-ns"
							style={{
								minWidth: `${cardMinWidth ?? smallCard}rem`
							}}>
							<CardComponent item={item} itemsCount={items.length} cloudinaryOptions={HOME_CAROUSEL_SMALL_OPTIONS} />
						</div>
					))}
				</Carousel>
			</div>

			{/* tablet/desktop */}
			<div className="dn db-ns">
				<Carousel
					rootMargin={rootMargin}
					shouldLockNavigation={widerCards && items.length <= 3 ? false : true}
					navigationVerticalPosition={navigationVerticalPosition}>
					{items.map((item, index) => (
						<Fragment key={`${item.fields?.title}-${index}`}>
							{/* desktop */}
							<div
								className="dn db-l mh1 mh2-ns"
								style={{
									minWidth: `${cardMinWidth ?? largeCard}rem`
								}}>
								{item && (
									<CardComponent item={item} itemsCount={items.length} cloudinaryOptions={HOME_CAROUSEL_LARGE_OPTIONS} />
								)}
							</div>
							{/* tablet */}
							<div
								className="dn db-m dn-l mh1 mh2-ns"
								style={{
									minWidth: `${cardMinWidth ?? mediumCard}rem`
								}}>
								<CardComponent item={item} itemsCount={items.length} cloudinaryOptions={HOME_CAROUSEL_MEDIUM_OPTIONS} />
							</div>
						</Fragment>
					))}
				</Carousel>
			</div>
		</section>
	) : null;
};
